import {createClient} from '@launchdarkly/react-sdk';
import type {LDContext, LDReactClient} from '@launchdarkly/react-sdk';
import type {FlagDef} from './types';

let ldClient: LDReactClient | null = null;

const SDK_KEY = process.env.REACT_APP_LD_CLIENT_ID;

const ldOptions = {
  diagnosticOptOut: true,
  privateAttributes: ['email'],
  applicationInfo: {
    id: 'bridge',
    version: process.env.REACT_APP_BRIDGE_VERSION,
  },
};

export const PLATFORM_CONTEXT = {
  key: 'bridge',
};

export const ANONYMOUS_CONTEXT: LDContext = {
  kind: 'multi',
  user: {anonymous: true, key: 'anonymous'},
  platform: PLATFORM_CONTEXT,
};

export type MeForContext = {
  id: string;
  global_id: string | null;
  membership?: {
    role: string;
    organization: {
      id: string;
      global_id: string | null;
      name: string;
    };
  };
  memberships: Array<{
    user: {email: string};
    organization: {
      id: string;
      global_id: string | null;
      name: string;
    };
  }>;
};

export function buildLDContext(me: MeForContext | null): LDContext {
  if (!me || !me.memberships) {
    return ANONYMOUS_CONTEXT;
  }

  // User context
  const userKey = me.global_id ?? me.id;
  const userContext: LDContext = {
    key: userKey,
    email: me.memberships[0].user.email,
  };

  // Memberships context.  Includes all orgs
  const organizationIds = me.memberships.map(
    ({organization}) => organization?.global_id || organization.id,
  );
  const organizationNames = me.memberships.map(
    ({organization}) => organization.name,
  );

  const activeMembership = me.membership;
  const activeOrg = activeMembership?.organization;

  return {
    kind: 'multi',
    user: userContext,
    memberships: {
      key: userKey,
      organizationIds,
      organizationNames,
    },
    ...(activeMembership &&
      activeOrg && {
        activeMembership: {
          key: userKey,
          organizationId: activeOrg.global_id ?? activeOrg.id,
          organizationName: activeOrg.name,
          role: activeMembership.role,
        },
      }),
    platform: PLATFORM_CONTEXT,
  } as LDContext;
}

export function createLDClient(): LDReactClient | null {
  if (!SDK_KEY) {
    console.log(
      'REACT_APP_LD_CLIENT_ID is not set - feature flags will fall through to defaults.',
    );
    return null;
  }

  const client = createClient(SDK_KEY, ANONYMOUS_CONTEXT, ldOptions);
  client.start();
  return client;
}

export function getLDClient(): LDReactClient | null {
  if (ldClient === null) {
    ldClient = createLDClient();
  }
  return ldClient;
}

// Imperative read path for use outside the React tree (MobX stores, API
// interceptors, utils). Mirrors the `useFeatureFlag` hook, which reads the same
// client from React context. Returns the default until `client.start()` has
// resolved; reactive UI should use the hook instead.
export function getBoolVariation(flag: FlagDef<boolean>): boolean {
  const client = getLDClient();
  return client
    ? client.boolVariation(flag.key, flag.defaultValue)
    : flag.defaultValue;
}
